import React from "react"
import {
  faBuilding,
  faChartLine,
  faMotorcycle,
  faReceipt,
  faSackDollar,
  faShop,
  faTruck,
  faUserGroup,
  faFileInvoiceDollar,
} from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { AdminDashboard } from "../../../Objects/AdminDashboard/AdminDashboard"
import { convertToLocalMoney } from "../../../Objects/Generics/Helps"
import { AdminMetricCard } from "../components/AdminMetricCard"
import { SeriesChart } from "../components/SeriesChart"

interface OverviewSectionProps {
  dashboard: AdminDashboard
}

export const OverviewSection: React.FC<OverviewSectionProps> = ({ dashboard }) => {
  const { t } = useTranslation("frontend")

  return (
    <div className="w-full overflow-auto bg-white h-100vh p-1 md:p-0">
      <div className="grid grid-cols-1 gap-3 p-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 min-[1800px]:grid-cols-5">
        <AdminMetricCard
          title={t("admin_dashboard.registered_users")}
          value={`${dashboard.overview.totalUsers}`}
          icon={faUserGroup}
          tone="info"
        />

        <AdminMetricCard
          title={t("admin_dashboard.registered_companies")}
          value={`${dashboard.overview.totalCompanies}`}
          icon={faBuilding}
          tone="brand"
        />

        <AdminMetricCard
          title={t("admin_dashboard.test_or_demo_companies")}
          value={`${dashboard.overview.totalTestOrDemoCompanies}`}
          icon={faBuilding}
          tone="warning"
        />

        <AdminMetricCard
          title={t("admin_dashboard.metric_eligible_companies")}
          value={`${dashboard.overview.totalMetricEligibleCompanies}`}
          icon={faShop}
          tone="positive"
        />

        <AdminMetricCard
          title={t("admin_dashboard.couriers")}
          value={`${dashboard.overview.totalCouriers}`}
          icon={faMotorcycle}
          tone="violet"
        />

        <AdminMetricCard
          title={t("admin_dashboard.total_orders")}
          value={`${dashboard.overview.totalOrders}`}
          icon={faReceipt}
          tone="brand"
        />

        <AdminMetricCard
          title={t("admin_dashboard.total_manual_external_orders")}
          value={`${dashboard.overview.totalManualExternalOrders}`}
          icon={faFileInvoiceDollar}
          tone="info"
        />

        <AdminMetricCard
          title={t("admin_dashboard.total_deliveries")}
          value={`${dashboard.overview.totalDeliveries}`}
          icon={faTruck}
          tone="violet"
        />

        <AdminMetricCard
          title={t("admin_dashboard.total_revenue_subscriptions")}
          value={convertToLocalMoney(dashboard.revenue.totalApprovedRevenue)}
          icon={faSackDollar}
          tone="positive"
        />

        <AdminMetricCard
          title={t("admin_dashboard.month_revenue")}
          value={convertToLocalMoney(dashboard.revenue.currentMonthApprovedRevenue)}
          icon={faChartLine}
          tone="positive"
        />

        <AdminMetricCard
          title={t("admin_dashboard.day_revenue")}
          value={convertToLocalMoney(dashboard.revenue.currentDayApprovedRevenue)}
          icon={faSackDollar}
          tone="brand"
        />

        <AdminMetricCard
          title={t("admin_dashboard.active_companies_30_days")}
          value={`${dashboard.platformUsage.activeCompaniesLast30Days}`}
          icon={faShop}
          tone="positive"
        />

        <AdminMetricCard
          title={t("admin_dashboard.active_customers_30_days")}
          value={`${dashboard.platformUsage.activeCustomersLast30Days}`}
          icon={faUserGroup}
          tone="info"
        />

        <AdminMetricCard
          title={t("admin_dashboard.created_orders_30_days")}
          value={`${dashboard.platformUsage.ordersCreatedLast30Days}`}
          icon={faReceipt}
          tone="brand"
        />

        <AdminMetricCard
          title={t("admin_dashboard.manual_external_orders_30_days")}
          value={`${dashboard.platformUsage.manualExternalOrdersCreatedLast30Days}`}
          icon={faFileInvoiceDollar}
          tone="info"
        />

        <AdminMetricCard
          title={t("admin_dashboard.manual_external_customers_30_days")}
          value={`${dashboard.platformUsage.manualExternalOrderCustomersLast30Days}`}
          icon={faUserGroup}
          tone="violet"
        />
      </div>

      <Animate type={AnimationTypes.fadeInTraslate} duration="2s">
        <div className="mt-4 grid grid-cols-1 gap-4 p-1 2xl:grid-cols-2 min-[2200px]:grid-cols-3">
          <SeriesChart
            title={t("admin_dashboard.monthly_revenue_evolution")}
            data={dashboard.monthlyRevenueSeries}
            money={true}
          />

          <SeriesChart title={t("admin_dashboard.orders_per_day_last_7_days")} data={dashboard.dailyOrdersSeries} />

          <SeriesChart
            title={t("admin_dashboard.monthly_company_growth")}
            data={dashboard.monthlyCompanyGrowthSeries}
          />
        </div>
      </Animate>
    </div>
  )
}
